import * as config from './config';
import BaseFactory from './BaseFactory';
import MemoryCache from './MemoryCache';
import { slot2in, in2slot } from './measurement';
import { 
    AXIS_X,
    dimensions,
    drawerBox,
    frameAndPanel,
    mirror,
    panel,
    shelfPanel,
    uvNormalize,
    uvTranspose,
    verticalBoringPanel,
} from './IIID';
import {
    DiagridBoxMesh,
    DiagridFaceMesh,
    GridFaceMesh,
    GridBoxMesh,
    ThreeJSMesh
} from './mw/prims';
import { Join, SetNormal, Translate } from './mw/ops';

import {
    Box2,
    Box3,
    BoxGeometry,
    BufferGeometry,
    ExtrudeGeometry,
    Mesh,
    Shape,
    Vector2,
    Vector3,
} from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import ModelLoader from './ModelLoader';
import logging from './logging';
import { Axis, resize, setOrigin } from './geometry';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { lerp } from './utils';
import traverse from './utils/traverseGenerator';


const log = logging("ParametricCache");
const ml = ModelLoader.getInstance();

type GeometryLibrary = Record<string, BufferGeometry>;

/**
 * Creates a single group for the given buffer geometry, effectively making the
 * Mesh using this geometry require a material array instead of a single
 * material.
 */
const singleGroup = (geo: BufferGeometry) => {
    const pos = geo.getAttribute('position');
    geo.addGroup(0, pos.count, 0);
};


/**
 * The ParametricCache ends up being the front-end for IIID.js. If you request
 * a piece of geometry in a specific style and dimensions, you'll get a
 * shared reference to any existing geometry with the same specifications.
 */
export class ParametricCache extends BaseFactory {

    geocache: MemoryCache< Promise<GeometryLibrary> >;
    results: MemoryCache<BufferGeometry>;
    loader: OBJLoader;
    baseUrl: string;

    constructor() {
        super();
        this.geocache = new MemoryCache();
        this.results = new MemoryCache();
        this.loader = new OBJLoader();
        this.baseUrl = config.geometry;
    }

    async make(name: string, ...args: unknown[]): Promise<BufferGeometry> {
        const key: string[] = args.map(x => String(x));
        let result = this.results.get(name, ...key);
        if( result == null ){
            result = await super.make(name, ...args);
            this.results.put(result, name, ...key);
        }
        return result;
    }

    private load(name: string) {
        const url = `${this.baseUrl}/${name}.obj`;

        const promise: Promise<GeometryLibrary> = new Promise(
            (resolve, reject) => {
                this.loader.load(url,
                    object => {
                        let geometry: GeometryLibrary = {};
                        object.name = name;
                        object.traverse(child => {
                            if(child instanceof Mesh) {
                                geometry[child.name] = child.geometry;
                                child.geometry.name = name;
                            }
                        });
                        resolve(geometry);
                    },
                    null, // no progress event updates, thus null
                    error => reject(error)
                );
            });

        this.geocache.put(promise, name);
        return promise;
    }

    // Returns an Object appropriate to pass as the geometry parameter to
    // IIID.js. 
    getgeo(name: string): Promise<GeometryLibrary> {
        let promise = this.geocache.get(name);
        if(promise == null) {
            promise = this.load(name);
        }
        return promise;
    }

    // This creates a new object with geometries from both the a and b names.
    // This helps with creating doors using the IIID.door() function, which
    // expects geometry for both the frame and panel in its geometry parameter.
    async union(a:string, b: string) {
        const 
            ag = await this.getgeo(a),
            bg = await this.getgeo(b);
        return {...ag, ...bg};
    }

}

const pc = new ParametricCache();

pc.extend('FrameAndPanel', async function(frameStyle: string, panelStyle: string, width: number, height: number, grain: string) {
    log.debug('ParametricCache::FrameAndPanel', ...arguments);
    let geometry: BufferGeometry;
    if(frameStyle)
        geometry = await this.union(frameStyle, panelStyle);
    else
        geometry = await this.getgeo(panelStyle);

    const [frameGeo, panelGeo] = frameAndPanel(
        geometry,
        new Vector2(width, height),
        frameStyle,
        panelStyle);

    // Set the grain direction by transposing the UVs for horizontal grain
    if(grain == 'horizontal') {
        uvTranspose(panelGeo);
    }

    return [frameGeo, panelGeo];
});

// This is a front facing panel like what appears on the front of a drawer,
// door or hook rack.
pc.extend('FrontPanel', async function(
    panelStyle: string, width: number, height:number, grain: 'horizontal' | 'vertical')
{
    const geometry = await this.getgeo(panelStyle);

    const panelGeo = panel(geometry, new Vector2(width, height));

    if(grain == 'horizontal') {
        uvTranspose(panelGeo);
    }

    return panelGeo;
});

// This is a panel with *depth* and height, i.e. an upright between sections
pc.extend('Panel', async function(panelStyle: string, depth: number, height: number, grain: string) {
    log.debug('ParametricCache::Panel', ...arguments);
    let geometry = await this.getgeo(panelStyle);
    var panelGeo = panel(geometry,
                    new Vector2(depth, height));

    if(grain == 'horizontal') {
        uvTranspose(panelGeo);
    }

    uvNormalize(panelGeo);
    return panelGeo;
});

pc.extend('DrawerBox', async function(width: number, height: number, depth: number, front){
    log.debug('ParametricCache::DrawerBox', ...arguments);
    return drawerBox(new Vector3(width, height, depth), front);
});

pc.extend('ScoopFront', async function(width: number, height: number) {
    log.debug('ParametricCache::ScoopFront', ...arguments);
    let geometry = await this.getgeo('panel_scoop_3x0.75');
    const [_, panelGeo] = frameAndPanel(
        geometry,
        new Vector2(width, height),
        null, // no frame
        'panel_scoop_3x0.75');
    uvNormalize(panelGeo);
    return [null, panelGeo];
});

pc.extend('FlatFront', async function(width: number, height: number) {
    log.debug('ParametricCache::FlatFront', ...arguments);
    let geometry = await this.getgeo('panel_plain_0.75in');
    const [_, panelGeo] = frameAndPanel(
        geometry,
        new Vector2(width, height),
        null, // no frame
        'panel_plain_0.75in');
    uvNormalize(panelGeo);
    return [null, panelGeo];
});

pc.extend('FixedShelf', async function(width: number, depth: number) {
    log.debug('ParametricCache::FixedShelf', ...arguments);
    const basis = await pc.make(
        'Resizable',
        `${this.baseUrl}/panel_resizable.glb`,
        width, depth, 0.75,
        0, 0, 0 // left bottom back
    );
    const clone = basis.clone();
    clone.rotateX(Math.PI/2);
    clone.translate(0, 0.75, 0);
    uvTranspose(clone, 0);
    uvNormalize(clone);
    return clone;
});

pc.extend('VerticalBoringPanel',
          async function(
              style: string,
              height: number,
              depth: number,
              mirrorPanel: boolean)
{
    log.debug('ParametricCache::VerticalBoringPanel', ...arguments);
    let geometry = await this.getgeo(style);
    let bgeo = verticalBoringPanel(
        geometry,
        new Vector2(depth, height),
        new Box2(
            new Vector2(2, slot2in(1)),
            new Vector2(depth-2, slot2in(in2slot(height)-1))
        )
    );
    // The result is laid out in the XY plane, but we want it in the XZ plane
    bgeo.rotateY(Math.PI/2);
    bgeo.translate(0, 0, depth);

    if(mirrorPanel) {
        bgeo = mirror(bgeo, AXIS_X);
    }

    return bgeo;
});

pc.extend('Divider', async function(height: number, depth: number) {
    log.debug('ParametricCache::Divider', ...arguments);
    const basis = await pc.make(
        'Resizable',
        `${this.baseUrl}/panel_resizable.glb`,
        depth, height, 0.75,
        0, 0, 0 // left bottom back
    );
    const clone = basis.clone();
    clone.rotateY(-Math.PI/2);
    clone.translate(0.75, 0, 0);
    uvNormalize(clone);
    return clone;
});

pc.extend('AdjustableShelf', async function(width, _height, depth) {
    log.debug('ParametricCache::AdjustableShelf', ...arguments);
    const basis = await pc.make(
        'Resizable',
        `${this.baseUrl}/panel_resizable.glb`,
        width, depth, 0.75,
        0, 0, 0 // left bottom back
    );
    const clone = basis.clone();
    clone.rotateX(Math.PI/2);
    clone.translate(0, 0.75, 0);
    uvTranspose(clone, 0);
    uvNormalize(clone);
    return clone;
});

pc.extend('GlassShelf', async function(width: number, _height, depth: number){
    log.debug('ParametricCache::GlassShelf', ...arguments);
    let geometry = await this.getgeo('panel_plain_0.25in');
    var shelf = shelfPanel(
        geometry,
        new Vector2(width, depth)
    );
    uvNormalize(shelf);
    singleGroup(shelf);
    return shelf;
});


pc.extend('FlatShoeShelf', async function(width, _height, depth) {
    log.debug('ParametricCache::FlatShoeShelf', ...arguments);
    const shelf = await this.make('AdjustableShelf', width, _height, depth);
    return shelf;
});

pc.extend('FixedShoeShelf', async function(width, _height, depth) {
    log.debug('ParametricCache::FixedShoeShelf', ...arguments);
    const shelf = await this.make('AdjustableShelf', width, _height, depth);
    const clone = shelf.clone();
    clone.translate(0, -3/8, 0);
    return clone;
});

pc.extend('SlantedShelfWithFence', async function(
    style: string, width: number, _height, depth: number)
{
    const 
        shelfAngle = Math.PI/180 * 15,
        rakeHeight = Math.sin(shelfAngle) * depth,
        shelfDepth = Math.sqrt(depth*depth + rakeHeight*rakeHeight) - 1.0,
        fence = await ml.get(`${config.models}/${style}`),
        shelf = (await this.make('FixedShelf', width, shelfDepth)).clone();

    let fenceGeo: BufferGeometry;
    fence.traverse(obj => {
        if(obj instanceof Mesh) {
            fenceGeo = obj.geometry.clone();
        }
    });

    fenceGeo.translate(11.5, 0.125, depth - 14);
    fenceGeo.scale(width/23, 1, 1);

    shelf.rotateX(shelfAngle);
    shelf.translate(0, rakeHeight, 0.5);
    const shelfGeo = shelf.toNonIndexed();
    const joined = mergeGeometries([shelfGeo, fenceGeo]);

    joined.groups = shelfGeo.groups;
    joined.addGroup(
        joined.groups[1].count,
        fenceGeo.getAttribute('position').count,
        2);

    return joined;
});

pc.extend('SlidingShelf', async function(width: number, _height, depth: number) {
    log.debug('ParametricCache::SlidingShelf', ...arguments);
    const fixed = await this.make('FixedShelf', width - 0.5, depth);
    const clone = fixed.clone();
    clone.translate(0.25, -3/8, 0);

    // The slide is a little smaller than the space available (by 0.2 in) so
    // that there is some recess to give it a readable silhouette against the
    // shelving material
    let slideLeft = new BoxGeometry(0.25, 0.55, depth - 0.2);
    // move the origin to the back left center
    slideLeft.translate(0.125, 0, depth/2);
    const slideRight = slideLeft.clone();
    slideRight.translate(width - 0.25, 0, 0);
    const slides = mergeGeometries([slideLeft, slideRight]);
    singleGroup(slides);
    slides.groups[0].materialIndex = 2;
    const joined = mergeGeometries([clone, slides]);

    joined.groups = clone.groups;
    const last = joined.groups[joined.groups.length - 1];
    joined.addGroup(
        last.start+last.count,
        slides.attributes.position.count,
        2);

    return joined;
});

pc.extend('HangBar', async function(filename: string, width: number) {
    log.debug('ParametricCache::HangBar', ...arguments);
    const scene = await ml.get(`${config.models}/${filename}`);
    let geo: BufferGeometry;
    for(const obj of traverse(scene)) {
        if(obj instanceof Mesh) {
            geo = obj.geometry.clone();
        }
    }
    if(geo == null) {
        log.error(`Unable to load mesh from "${filename}"`);
        return;
    }

    if(geo.boundingBox == null) geo.computeBoundingBox();
    const dims = dimensions(geo);
    const scale = width / dims.x;
    geo.scale(scale, 1, 1);
    setOrigin(geo, 0, 0.5, 0.5);
    singleGroup(geo);
    return geo;
});


pc.extend('CubbyBoxes', async function(
    countX: number, countY: number, diagonal: boolean, width: number, height: number, depth: number, thickness: number)
{
    let boxes: BufferGeometry;
    if(diagonal) {
        const uWidth = width / countX;
        let uHeight = height / countY;

        let unitB = new DiagridBoxMesh(
            uWidth, uHeight, depth, thickness);

        let bs = [];
        for(let x = 0; x < countX; x++) {
            for(let y = 0; y < countY; y++) {
                bs.push(new Translate(uWidth*x, uHeight*y, 0, unitB));
            }
        }
        boxes = ThreeJSMesh.toBufferGeometry((new Join(bs)).generate());
    } else {
        const boxMesh = new GridBoxMesh(width, height, depth,
            thickness, countX, countY);
        boxes = ThreeJSMesh.toBufferGeometry(boxMesh.generate());
    }

    return boxes;
});

pc.extend('CubbyFace', async function(
    countX: number, countY: number, diagonal: boolean, width: number, height: number, depth: number, thickness: number)
{
    let face: BufferGeometry;
    if(diagonal) {
        const uWidth = width / countX;
        let uHeight = height / countY;
        const unitMesh = new DiagridFaceMesh(uWidth, uHeight, thickness);
        const unitF = new SetNormal(0, 0, 1, unitMesh);

        let fs = [];
        for(let x = 0; x < countX; x++) {
            for(let y = 0; y < countY; y++) {
                fs.push(new Translate(uWidth*x, uHeight*y, depth, unitF));
            }
        }
        face = ThreeJSMesh.toBufferGeometry((new Join(fs)).generate());
    } else {
        const faceMesh = new SetNormal(0, 0, 1,
            new Translate(0, 0, depth,
                new GridFaceMesh(width, height, thickness,
                    countX, countY)));
        face = ThreeJSMesh.toBufferGeometry(faceMesh.generate());
    }

    return face;
});

pc.extend('ToeKick', async function(
    width: number, height: number, depth: number)
{
    const basis = await pc.make(
        'Resizable',
        `${this.baseUrl}/panel_resizable.glb`,
        width, height, 0.75,
        0, 0, 0 // left bottom back
    );
    const clone = basis.clone();
    clone.translate(0, 0, depth);
    uvTranspose(clone, 0);
    uvNormalize(clone);
    return clone;
});


/**
 * Note that this will return an indexed mesh, so it is unlikely to work
 * directly with the IIID functions, which all expect triangle meshes.
 */
pc.extend('Resizable', async function(
        url: string,
        width: number = -1, height: number = -1, depth: number = -1,
        ox: number = 0, oy: number = 0, oz: number = 0
    )
{
    const
        scene = (await ml.get(url)),
        geometries: BufferGeometry[] = [],
        bb = new Box3();

    scene.traverse(obj => {
        if((obj as any).isMesh) {
            const geo = (obj as Mesh).geometry.clone();
            geo.name = url;
            geo.computeBoundingBox();
            bb.expandByPoint(geo.boundingBox.min);
            bb.expandByPoint(geo.boundingBox.max);
            geometries.push(geo);
        }
    });

    let dx = 0, dy = 0, dz = 0;
    if(width != -1) {
        dx = width - (bb.max.x - bb.min.x);
    }
    if(height != -1) {
        dy = height - (bb.max.y - bb.min.y);
    }
    if(depth != -1) {
        dz = depth - (bb.max.z - bb.min.z);
    }

    for(const geo of geometries) {
        if(dx != 0) {
            resize(geo, dx, Axis.x);
        }
        if(dy != 0) {
            resize(geo, dy, Axis.y);
        }
        if(dz != 0) {
            resize(geo, dz, Axis.z);
        }
    }
    
    const result = mergeGeometries(geometries, true);
    setOrigin(result, ox, oy, oz);
    // vertex colors are used for resize, not needed for display, and will
    // prevent merging with other geometries
    result.deleteAttribute('color'); 

    return result;
});

type Space = {
    start: number,
    width: number,
    open: boolean,
}

const resample = (heights: number[], x: number) => {
    if(heights.length < 2) {
        log.error('ParametricCache resample() received invalid heights');
        return 0;
    }
    for(let i=0; i+3 < heights.length; i += 2) {
        const
            ax = heights[i],
            ah = heights[i+1],
            bx = heights[i+2],
            bh = heights[i+3];
        if(x >= ax && x <= bx) {
            const t = (x - ax) / (bx - ax);
            return lerp(ah, bh, isNaN(t) ? 0 : t);
        }
        //else if(x > bx) {
        //    return bh;
        //}
    }
    if(heights.length >= 2 && x <= heights[1]) {
        return heights[1];
    }
}

// To support the simple caching mechanism, we expect a flattened array of
// (x, height) pairs, i.e. the array should be interleaved so it looks like
// [x1, height1, ... xN, heightN].
pc.extend('Wall', async function(heights: number[], spaces: Space[])
{
    const geos: BufferGeometry[] = [];
    for(const space of spaces) {
        const
            shape = new Shape(),
            start = Math.max(space.start, heights[0]),
            end = space.start + space.width;
        shape.moveTo(start, 0);
        shape.lineTo(start, resample(heights, start));
        for(let i = 0; i < heights.length; i += 2) {
            const x = heights[i];
            if(x < space.start) continue;
            if(x > end) break;

            const height = heights[i+1];
            shape.lineTo(x, height);
        }
        shape.lineTo(end, resample(heights, end));
        shape.lineTo(end, 0);

        const geo = new ExtrudeGeometry(shape, {
            depth: 4.5,
            bevelEnabled: false,
        });
        geo.translate(0, 0, -4.5);

        geos.push(geo);
    }
    return geos;
});

// The origin of the resulting object should be placed where the two walls meet,
// i.e. at the position of the second wall in the pair.
pc.extend('WallCornerFill', async function(
    height: number,
    ax: number, ay: number,
    bx: number, by: number,
    cx: number, cy: number,
    )
{
    const shape = new Shape();
    shape.moveTo(0, 0);
    shape.lineTo(ax, ay);
    // c stands for 'corner', which is why it's in the middle
    shape.lineTo(cx, cy);
    shape.lineTo(bx, by);

    const geo = new ExtrudeGeometry(shape, {
        depth: height,
        bevelEnabled: false,
        });
    geo.rotateX(-Math.PI/2);
    return geo;
});

export default pc;
